import circleBg from '@/assets/notifications/order-filled/medallion-circle-bg.svg';
import checkIllustration from '@/assets/notifications/order-filled/medallion-check.png';
import { POPUP_CTA } from '@/lib/notifications/popup-cta';
import { marketTitle, metaNumber, metaString, priceCentsLabel } from '@/lib/notifications/meta';
import { Medallion } from '../Medallion';
import { PopupCta } from '../PopupCta';
import type { PopupViewProps } from '../types';

const CTA = POPUP_CTA['order-filled'];

/**
 * ORDER_FILLED — fully filled. Positive (green) tone: green check medallion, a
 * bold "fully filled" line in green, and a "View Position" CTA that deep-links
 * to the market. Reference build for the "simple green confirmation" pattern.
 */
export function OrderFilledPopup({ notification, onAction }: PopupViewProps) {
  const meta = notification.meta;
  const title = marketTitle(meta);
  // Buy/sell verb comes from `action` (`meta.side` is the YES/NO outcome, not the verb).
  const verb = metaString(meta, 'action')?.toLowerCase() === 'sell' ? 'sell' : 'buy';
  const qty = metaNumber(meta, 'filledQuantity') ?? metaNumber(meta, 'orderQuantity');
  const price = metaNumber(meta, 'avgPrice');

  return (
    <>
      <Medallion circleSrc={circleBg} illustrationSrc={checkIllustration} tone="positive" alt="" />

      <div className="flex flex-col items-center gap-2">
        <h2 className="text-3xl font-semibold tracking-[-0.6px] text-ink">Order Filled</h2>

        {title && <p className="text-xl font-bold text-ink">{`“${title}”`}</p>}

        {(qty !== undefined || price !== undefined) && (
          <p className="text-xl text-ink">
            Your {verb} order for{' '}
            <span className="font-bold">
              {qty !== undefined ? `${qty} contracts` : 'your contracts'}
              {price !== undefined ? ` @ ${priceCentsLabel(price)}` : ''}
            </span>
          </p>
        )}

        <p className="text-[28px] font-bold leading-tight text-success">has been fully filled.</p>
        <p className="text-xl text-ink">Your position is now in your portfolio.</p>
      </div>

      <PopupCta
        label={CTA.primary.label}
        width={234}
        onClick={() => onAction(CTA.primary.action)}
        testId="popup-cta-primary"
      />
    </>
  );
}
